import { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { auth, organizations } from '../lib/api'
import { buildSubdomainUrl } from '../lib/utils'

export default function MagicLinkHandler() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('verifying') // verifying, redirecting, error
  const [message, setMessage] = useState('')
  const hasVerified = useRef(false)

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token')

      if (!token) {
        setStatus('error')
        setMessage('No verification token found.')
        return
      }

      // Prevent multiple verification attempts
      if (hasVerified.current) {
        return
      }
      hasVerified.current = true

      try {
        // Verify the magic link - this sets the auth cookie
        const result = await auth.verifyMagicLink(token)
        const user = result?.user

        if (!user) {
          throw new Error('Verification failed - no user returned')
        }

        // Cache auth state in sessionStorage BEFORE redirect
        // This ensures UserContext has correct initial state on next page load
        sessionStorage.setItem('iceops_user', JSON.stringify(user))
        sessionStorage.setItem('iceops_auth', 'true')

        // Broadcast auth success to other tabs (closes the login tab)
        try {
          const channel = new BroadcastChannel('iceops_auth')
          channel.postMessage({ type: 'auth_success', user })
          channel.close()
        } catch (e) {
          // BroadcastChannel not supported, use localStorage fallback
          localStorage.setItem('iceops_auth_event', Date.now().toString())
        }

        // Show redirecting status
        setStatus('redirecting')

        // Default to dashboard on current domain
        let redirectUrl = '/dashboard'

        // Admin goes to admin page
        if (user && user.role === 'admin') {
          redirectUrl = '/admin'
        } else {
          // Non-admin users: try to redirect to their org subdomain
          try {
            const userOrgs = await organizations.getAll()
            if (userOrgs && userOrgs.length > 0) {
              const org = userOrgs[0]
              if (org && org.slug) {
                // Build full URL with /dashboard path
                const subdomainBase = buildSubdomainUrl(org.slug)
                redirectUrl = subdomainBase + '/dashboard'
              }
            }
          } catch (orgError) {
            // If org fetch fails, stay on current domain's dashboard
            console.error('Error fetching organizations:', orgError)
          }
        }

        // Immediate redirect - use href for cross-origin, replace for same-origin
        if (redirectUrl.startsWith('http')) {
          window.location.href = redirectUrl
        } else {
          window.location.replace(redirectUrl)
        }
      } catch (error) {
        console.error('Magic link verification error:', error)
        setStatus('error')
        setMessage(error.message || 'Invalid or expired sign-in link.')
      }
    }

    verifyToken()
  }, [searchParams])

  // Show spinner for both verifying and redirecting states
  const isLoading = status === 'verifying' || status === 'redirecting'

  return (
    <div className="max-w-md mx-auto mt-6">
        <div className="card text-center">
          {isLoading && (
            <>
              <div className="inline-block mb-6">
                <svg
                  className="animate-spin h-16 w-16 text-ice-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {status === 'redirecting' ? 'Signing you in...' : 'Verifying your sign-in link...'}
              </h2>
              <p className="text-slate-600">
                {status === 'redirecting' ? 'You will be redirected shortly.' : 'Please wait while we confirm your identity.'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-block mb-6 text-red-600">
                <svg className="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Sign-in failed
              </h2>
              <p className="text-slate-600 mb-6">{message}</p>
              <a
                href="/login"
                className="btn-primary inline-block"
              >
                Request New Sign-In Link
              </a>
            </>
          )}
        </div>
    </div>
  )
}
